import { 
    collection, 
    addDoc, 
    getDocs, 
    doc, 
    updateDoc, 
    deleteDoc, 
    getDoc,
    query,
    where,
    orderBy,
    Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { VATTax, VATTaxCreateInput, VATTaxUpdateInput, ProductVAT, VATReport } from '../types/vat';

class VATService {
    private collectionName = '8am-vat-taxes';
    private productVATCollectionName = '8am-product-vat';

    // Quản lý thuế VAT
    async createVATTax(vatTax: VATTaxCreateInput): Promise<VATTax> {
        try {
            // Loại bỏ các trường có giá trị undefined để tránh lỗi Firebase
            const cleanedVatTax = Object.fromEntries(
                Object.entries(vatTax).filter(([_, value]) => value !== undefined)
            );

            const vatTaxData = {
                ...cleanedVatTax,
                isActive: vatTax.isActive ?? true,
                isDefault: vatTax.isDefault ?? false,
                effectiveDate: vatTax.effectiveDate ? Timestamp.fromDate(vatTax.effectiveDate) : null,
                expiredDate: vatTax.expiredDate ? Timestamp.fromDate(vatTax.expiredDate) : null,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            const docRef = await addDoc(collection(db, this.collectionName), vatTaxData);
            
            return {
                id: docRef.id,
                ...vatTax,
                isActive: vatTax.isActive ?? true,
                isDefault: vatTax.isDefault ?? false,
                createdAt: new Date(),
                updatedAt: new Date()
            };
        } catch (error) {
            console.error('Error creating VAT tax:', error);
            throw new Error('Không thể tạo thuế VAT mới');
        }
    }

    async getAllVATTaxes(): Promise<VATTax[]> {
        try {
            const q = query(
                collection(db, this.collectionName),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    effectiveDate: data.effectiveDate?.toDate(),
                    expiredDate: data.expiredDate?.toDate(),
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate()
                } as VATTax;
            });
        } catch (error) {
            console.error('Error fetching VAT taxes:', error);
            throw new Error('Không thể tải danh sách thuế VAT');
        }
    }

    async getActiveVATTaxes(): Promise<VATTax[]> {
        try {
            const q = query(
                collection(db, this.collectionName),
                where('isActive', '==', true),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    effectiveDate: data.effectiveDate?.toDate(),
                    expiredDate: data.expiredDate?.toDate(),
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate()
                } as VATTax;
            });
        } catch (error) {
            console.error('Error fetching active VAT taxes:', error);
            throw new Error('Không thể tải danh sách thuế VAT hoạt động');
        }
    }

    async getVATTaxById(id: string): Promise<VATTax | null> {
        try {
            const docRef = doc(db, this.collectionName, id);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    ...data,
                    effectiveDate: data.effectiveDate?.toDate(),
                    expiredDate: data.expiredDate?.toDate(),
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate()
                } as VATTax;
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching VAT tax by ID:', error);
            throw new Error('Không thể tải thông tin thuế VAT');
        }
    }

    async updateVATTax(vatTax: VATTaxUpdateInput): Promise<void> {
        try {
            const { id, ...updateData } = vatTax;
            const docRef = doc(db, this.collectionName, id);
            
            // Loại bỏ các trường có giá trị undefined để tránh lỗi Firebase
            const cleanedUpdateData = Object.fromEntries(
                Object.entries(updateData).filter(([_, value]) => value !== undefined)
            );
            
            const vatTaxData = {
                ...cleanedUpdateData,
                effectiveDate: updateData.effectiveDate ? Timestamp.fromDate(updateData.effectiveDate) : null,
                expiredDate: updateData.expiredDate ? Timestamp.fromDate(updateData.expiredDate) : null,
                updatedAt: Timestamp.now()
            };

            await updateDoc(docRef, vatTaxData);
        } catch (error) {
            console.error('Error updating VAT tax:', error);
            throw new Error('Không thể cập nhật thuế VAT');
        }
    }

    async deleteVATTax(id: string): Promise<void> {
        try {
            const docRef = doc(db, this.collectionName, id);
            await deleteDoc(docRef);
        } catch (error) {
            console.error('Error deleting VAT tax:', error);
            throw new Error('Không thể xóa thuế VAT');
        }
    }

    async toggleVATTaxStatus(id: string, isActive: boolean): Promise<void> {
        try {
            const docRef = doc(db, this.collectionName, id);
            await updateDoc(docRef, {
                isActive,
                updatedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error toggling VAT tax status:', error);
            throw new Error('Không thể thay đổi trạng thái thuế VAT');
        }
    }

    async setDefaultVATTax(id: string): Promise<void> {
        try {
            // Tắt tất cả thuế VAT mặc định hiện tại
            const allVATTaxes = await this.getAllVATTaxes();
            const updatePromises = allVATTaxes.map(async (vat) => {
                if (vat.id && vat.isDefault) {
                    const docRef = doc(db, this.collectionName, vat.id);
                    await updateDoc(docRef, {
                        isDefault: false,
                        updatedAt: Timestamp.now()
                    });
                }
            });
            
            await Promise.all(updatePromises);
            
            // Đặt thuế VAT mới làm mặc định
            const docRef = doc(db, this.collectionName, id);
            await updateDoc(docRef, {
                isDefault: true,
                isActive: true, // Đảm bảo thuế mặc định luôn hoạt động
                updatedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error setting default VAT tax:', error);
            throw new Error('Không thể đặt thuế VAT mặc định');
        }
    }

    // Lấy thuế VAT theo loại sản phẩm
    async getVATTaxByCategory(category: string): Promise<VATTax[]> {
        try {
            const q = query(
                collection(db, this.collectionName),
                where('applicableCategories', 'array-contains', category),
                where('isActive', '==', true)
            );
            const querySnapshot = await getDocs(q);
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    effectiveDate: data.effectiveDate?.toDate(),
                    expiredDate: data.expiredDate?.toDate(),
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate()
                } as VATTax;
            });
        } catch (error) {
            console.error('Error fetching VAT taxes by category:', error);
            throw new Error('Không thể tải thuế VAT theo loại sản phẩm');
        }
    }

    // Lấy thuế VAT mặc định
    async getDefaultVATTax(): Promise<VATTax | null> {
        try {
            const q = query(
                collection(db, this.collectionName),
                where('isDefault', '==', true),
                where('isActive', '==', true)
            );
            const querySnapshot = await getDocs(q);
            
            if (querySnapshot.empty) {
                return null;
            }
            
            const doc = querySnapshot.docs[0];
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                effectiveDate: data.effectiveDate?.toDate(),
                expiredDate: data.expiredDate?.toDate(),
                createdAt: data.createdAt?.toDate(),
                updatedAt: data.updatedAt?.toDate()
            } as VATTax;
        } catch (error) {
            console.error('Error fetching default VAT tax:', error);
            throw new Error('Không thể tải thuế VAT mặc định');
        }
    }

    // Tính toán thuế VAT cho sản phẩm
    calculateVAT(amount: number, vatRate: number): number {
        return Math.round((amount * vatRate / 100) * 100) / 100;
    }

    // Tính tổng tiền bao gồm VAT
    calculateTotalWithVAT(amount: number, vatRate: number): number {
        const vatAmount = this.calculateVAT(amount, vatRate);
        return Math.round((amount + vatAmount) * 100) / 100;
    }

    // Tính tiền chưa bao gồm VAT từ tổng tiền
    calculateAmountExcludingVAT(totalAmount: number, vatRate: number): number {
        const divisor = 1 + (vatRate / 100);
        return Math.round((totalAmount / divisor) * 100) / 100;
    }

    // Tạo dữ liệu mẫu cho test
    async createSampleData(): Promise<void> {
        try {
            const sampleVATTaxes: VATTaxCreateInput[] = [
                {
                    name: 'Thuế VAT hạt cà phê',
                    rate: 5,
                    description: 'Mức thuế VAT áp dụng cho hạt cà phê thô và rang xay',
                    applicableCategories: ['coffee'],
                    isActive: true,
                    isDefault: false
                },
                {
                    name: 'Thuế VAT đồ uống',
                    rate: 8,
                    description: 'Mức thuế VAT áp dụng cho đồ uống đóng chai và các loại nước giải khát',
                    applicableCategories: ['bottledDrink', 'dish'],
                    isActive: true,
                    isDefault: true
                },
                {
                    name: 'Thuế VAT thực phẩm',
                    rate: 10,
                    description: 'Mức thuế VAT áp dụng cho thực phẩm và món ăn',
                    applicableCategories: ['dish'],
                    isActive: true,
                    isDefault: false
                },
                {
                    name: 'Thuế VAT đặc biệt',
                    rate: 15,
                    description: 'Mức thuế VAT cao áp dụng cho các sản phẩm cao cấp',
                    applicableCategories: ['coffee', 'bottledDrink', 'dish'],
                    isActive: false,
                    isDefault: false
                }
            ];

            for (const vatTax of sampleVATTaxes) {
                await this.createVATTax(vatTax);
            }

            console.log('Sample VAT data created successfully');
        } catch (error) {
            console.error('Error creating sample VAT data:', error);
            throw new Error('Không thể tạo dữ liệu mẫu');
        }
    }
}

export const vatService = new VATService(); 